import type { Entries } from './catalog';

/**
 * The copy of one area of the interface: the account home, personal access tokens, deleting an account and the ways between products (ACC-08, ACC-18, ACC-21, ACC-22, ARR-07, ARR-16, ARR-17, ARR-25).
 *
 * Each area keeps its own file so its keys travel with its screens; a key here must not repeat one of
 * `en.ts`, `es.ts` or another area.
 */
export const home: Record<'en' | 'es', Entries> = {
	en: {
		'home.name.title': 'Your name',
		'home.setup.title': 'Set up your account',
		'palette.places': 'Go to',
		'palette.actions': 'Actions',
		'palette.signout': 'Sign out of Beyond',
		'home.setup.name': 'Add your name',
		'home.setup.name.action': 'Add your name',
		'home.setup.factor': 'Add a second factor',
		'home.setup.factor.detail': 'So a password alone cannot open your account.',
		'home.setup.factor.action': 'Add a second factor',
		'home.setup.organization': 'Create or join an organization',
		'home.security.title': 'Sign-in and security',
		'home.security.ways': { one: 'One way to sign in', other: '{count} ways to sign in' },
		'home.products.none': 'No products to open here yet.',
		'home.security.open': 'Review security',
		'tokens.title': 'Your tokens',
		'tokens.new': 'New token',
		'tokens.name': 'Name',
		'tokens.scopes': 'Scopes',
		'tokens.name.help': 'Say where it is used, such as a CI job.',
		'tokens.scopes.help': 'What it may do: a product and one of its scopes, such as cdn:publish, separated by spaces. Each product documents its scopes.',
		'tokens.organization': 'Organization',
		'tokens.organization.any': 'Every organization I belong to',
		'tokens.organization.help': 'It can act in each one you belong to, now or later.',
		'tokens.organization.every': 'Every organization',
		'tokens.days': 'Expires after',
		'tokens.days.count': { one: '1 day', other: '{count} days' },
		'tokens.create': 'Create token',
		'tokens.created': 'Copy your token now. It is shown only this once.',
		'tokens.done': 'Done',
		'tokens.check': 'Check your tokens',
		'tokens.secret': 'token',
		'tokens.none': 'No tokens.',
		'tokens.none.lead': 'A token lets automation, such as a CI job, act for you.',
		'tokens.expires': 'expires {date}',
		'tokens.used': 'last used {date}',
		'tokens.revoke': 'Revoke',
		'tokens.revoke.title': 'Revoke {name}?',
		'tokens.revoke.body': 'Anything using it stops working at once.'
	},
	es: {
		'home.name.title': 'Tu nombre',
		'home.setup.title': 'Prepara tu cuenta',
		'palette.places': 'Ir a',
		'palette.actions': 'Acciones',
		'palette.signout': 'Cerrar sesión en Beyond',
		'home.setup.name': 'Añade tu nombre',
		'home.setup.name.action': 'Añadir tu nombre',
		'home.setup.factor': 'Añade un segundo factor',
		'home.setup.factor.detail': 'Para que una contraseña sola no abra tu cuenta.',
		'home.setup.factor.action': 'Añadir un segundo factor',
		'home.setup.organization': 'Crea una organización o únete a una',
		'home.security.title': 'Acceso y seguridad',
		'home.security.ways': { one: 'Una forma de iniciar sesión', other: '{count} formas de iniciar sesión' },
		'home.products.none': 'Todavía no hay productos para abrir aquí.',
		'home.security.open': 'Revisar seguridad',
		'tokens.title': 'Tus tokens',
		'tokens.new': 'Token nuevo',
		'tokens.name': 'Nombre',
		'tokens.scopes': 'Alcances',
		'tokens.name.help': 'Indica dónde se usa, por ejemplo un trabajo de CI.',
		'tokens.scopes.help': 'Lo que puede hacer: un producto y uno de sus alcances, por ejemplo cdn:publish, separados por espacios. Cada producto documenta sus alcances.',
		'tokens.organization': 'Organización',
		'tokens.organization.any': 'Todas las organizaciones a las que pertenezco',
		'tokens.organization.help': 'Puede actuar en cada una a la que pertenezcas, ahora o más adelante.',
		'tokens.organization.every': 'Todas las organizaciones',
		'tokens.days': 'Vence después de',
		'tokens.days.count': { one: '1 día', other: '{count} días' },
		'tokens.create': 'Crear token',
		'tokens.created': 'Copia tu token ahora. Solo se muestra esta vez.',
		'tokens.done': 'Listo',
		'tokens.check': 'Revisar tus tokens',
		'tokens.secret': 'token',
		'tokens.none': 'No hay tokens.',
		'tokens.none.lead': 'Un token permite que una automatización, como un trabajo de CI, actúe por ti.',
		'tokens.expires': 'vence el {date}',
		'tokens.used': 'usado por última vez el {date}',
		'tokens.revoke': 'Revocar',
		'tokens.revoke.title': '¿Revocar {name}?',
		'tokens.revoke.body': 'Lo que lo use deja de funcionar al instante.'
	}
};
